import React from 'react';
import { getAuthHeaders } from '../../globals.js';
import { badgeSt, mutedSt } from '../diagnostic/shared.jsx';
import { KNOWLEDGE_LEVELS, LIFE_CONTENT_MAX_CHARS, LIFE_FRAGMENTS_MAX, TRANSFER_FRAGMENTS_MAX } from '../../ownerSpaceConfig.js';

// Mi espacio — the API call, error messages and small shared pieces.
// The server decides who enters; the screens only show it.

const ERRORS = {
  unauthorized: 'Tu sesión venció. Vuelve a iniciar sesión.',
  forbidden: 'Este espacio es privado: solo lo abre su dueña.',
  not_found: 'No se encontró. Actualiza la lista.',
  too_large: 'El contenido es demasiado grande para enviarlo de una vez.',
  schema_pending: 'Faltan las tablas de Mi espacio en Airtable: aplica la migración (docs/mi-espacio/configuracion.md).',
  owner_base_invalid: 'OIKE_OWNER_BASE_ID no es una base válida. Revisa la configuración.',
  test_base_missing: 'Entorno de prueba sin base de prueba configurada (OIKE_CENTRAL_BASE_ID).',
  'invalid_input: missing title': 'Falta el título.',
  'invalid_input: missing text': 'Escribe el texto.',
  'invalid_input: missing idea': 'Escribe la idea que quieres aplicar.',
  'invalid_input: missing question': 'Escribe tu pregunta.',
  'invalid_input: empty reflection': 'Responde al menos una pregunta (ninguna es obligatoria, pero la reflexión no puede quedar vacía).',
  'invalid_input: invalid page': 'La página tiene que ser un número.',
  'invalid_input: invalid time': 'El minuto no es válido (usa 12:30).',
  'invalid_input: too many fragments': `Hasta ${LIFE_FRAGMENTS_MAX} fragmentos por material (y ${TRANSFER_FRAGMENTS_MAX} para transferir).`,
  'invalid_input: content too long': `El contenido de un material llega hasta ${LIFE_CONTENT_MAX_CHARS.toLocaleString('es')} caracteres. No se guardó la última parte.`,
  'invalid_input: too many adjustments': 'Hasta tres ajustes por semana.',
  'invalid_input: ai failed': 'La IA no respondió. Vuelve a intentarlo.',
  'invalid_input: ai unavailable': 'La IA no está configurada en este entorno.',
  'invalid_input: invalid weekStart': 'Elige una semana.',
};

export async function ownerCall(body) {
  let res;
  try { res = await fetch('/api/owner-space', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) }); }
  catch {
    // The request never reached Oike (connection lost, network changed): nothing was done on the server
    throw Object.assign(new Error('No hubo conexión con Oike: revisá tu red y volvé a intentarlo.'), { code: 'network', status: 0, data: {} });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = data?.error || '';
    // A missing field says which one (Airtable names it); only a missing table speaks of the migration
    const text = code === 'schema_pending' && data.field ? `Falta el campo «${data.field}» en Airtable. Pedíselo a Omni (docs/mi-espacio/airtable-schema.md).` : ERRORS[code];
    const err = new Error(text || (code.startsWith('invalid_input: too long') ? 'Un texto pasa del largo máximo.' : code.startsWith('invalid_input') ? 'Hay un dato inválido.' : res.status === 403 ? ERRORS.forbidden : 'No se pudo completar. Vuelve a intentarlo.'));
    err.code = code; err.status = res.status; err.data = data;
    throw err;
  }
  return data;
}

const levelColor = { reference: 'rgba(30,42,44,0.06)', with_notes: 'rgba(96,165,250,0.15)', with_content: 'rgba(91,191,181,0.18)' };
export function LevelBadge({ level, withHint = false }) {
  const l = KNOWLEDGE_LEVELS.find(x => x.key === level) || KNOWLEDGE_LEVELS[0];
  return (
    <span>
      <span style={{ ...badgeSt(level === 'with_content'), background: levelColor[level], color: level === 'reference' ? 'var(--globant-muted)' : level === 'with_notes' ? '#2D5F99' : 'var(--globant-green)' }} title={l.hint}>{l.label}</span>
      {withHint && <span style={{ ...mutedSt, marginLeft: 6 }}>{l.hint}</span>}
    </span>
  );
}

/** "Qué es privado / qué usa la IA / qué se transfiere": always visible where it matters. */
export function Notice({ icon, children, tone = 'info' }) {
  const colors = { info: 'rgba(96,165,250,0.08)', private: 'rgba(91,191,181,0.08)', ai: 'rgba(191,215,48,0.08)', transfer: 'rgba(245,158,11,0.08)' };
  const borders = { info: 'rgba(96,165,250,0.35)', private: 'rgba(91,191,181,0.35)', ai: 'rgba(191,215,48,0.35)', transfer: 'rgba(245,158,11,0.45)' };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.5, padding: '8px 10px', borderRadius: 8, background: colors[tone], border: `1px solid ${borders[tone]}`, color: 'var(--globant-text)', margin: '8px 0' }}>
      <span aria-hidden="true">{icon}</span><div>{children}</div>
    </div>
  );
}

export const chipSt = (on) => ({
  font: 'inherit',
  border: `1px solid ${on ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: on ? 'rgba(91,191,181,0.12)' : 'transparent',
  color: on ? 'var(--globant-green)' : 'var(--globant-text)', borderRadius: 16, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
});

export const listBtnSt = { font: 'inherit', display: 'block', width: '100%', textAlign: 'left', background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, color: 'var(--globant-text)', cursor: 'pointer', marginBottom: 8 };

export const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
